import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { AmorphicService } from '../../../../services/amorphic/amorphic.service';
import { GeoChangeService } from '../../../../services/geoChange/geoChange.service';
import { Subscription } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AccountCenterControllerInterface } from '../../../../interfaces/account-center/AccountCenterControllerInterface';
import { ValidatorService } from '../../../../../../common/angular-shared/services/validators-reactive.service';
import { distinctUntilChanged } from 'rxjs/operators';
import { AccountCenterService } from '../../../../services/account-center/account-center.service';

@Component({
  moduleId: module.id,
  selector: 'legal-info-correction-form',
  templateUrl: './legal-info-form.component.html',
})
export class LegalInfoFormComponent implements OnInit {
  @Output() toggleLoadingSpinner: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() submitCorrection: EventEmitter<boolean> = new EventEmitter<boolean>();
  legalInfoForm: FormGroup;
  oldZip: string;
  accountCenterController: AccountCenterControllerInterface;
  personType: string;
  controller;
  person;
  verificationAddress;
  headerText: string = 'Name and Address changes';
  nameFieldText: string = 'Do you have a different legal name?';
  addressText: string = 'Has your legal address changed in the past two years?';

  public legalOptions = [
    { value: true, description: 'Yes' },
    { value: false, description: 'No' },
  ];

  public addressOptions = [
    { value: true, description: 'Yes' },
    { value: false, description: 'No' },
  ];

  constructor(
    private formBuilder: FormBuilder,
    private amorphicService: AmorphicService,
    private geoChangeService: GeoChangeService
  ) {
    this.controller = amorphicService.controller;
    this.accountCenterController = this.controller.accountCenterController;

    this.legalInfoForm = this.formBuilder.group({
      hasLegal: [''],
      hasAddress: [''],
      legalFirstName: ['', Validators.required],
      legalMiddleName: [''],
      legalLastName: ['', Validators.required],
      street: ['', Validators.required],
      line1: [''],
      city: ['', Validators.required],
      zip: ['', [Validators.required, ValidatorService.zipValidator]],
      state: ['', Validators.required],
    });
  }

  ngOnInit() {
    this.setPersonAndAddress();
    this.initializeLegalInfoForm();

    this.legalInfoForm
      .get('zip')
      .valueChanges.pipe(distinctUntilChanged())
      .subscribe(() => {
        this.setZip();
      });
  }

  shouldBeDisabled(): boolean {
    const hasLegal = this.legalInfoForm.get('hasLegal').value;
    const hasAddress = this.legalInfoForm.get('hasAddress').value;

    if (hasLegal === '' || hasAddress === '') {
      return true;
    }

    if (hasLegal === 'true') {
      if (this.legalInfoForm.get('legalFirstName').errors || this.legalInfoForm.get('legalLastName').errors) {
        return true;
      }
    }

    if (hasAddress === 'true') {
      if (
        this.legalInfoForm.get('street').errors ||
        this.legalInfoForm.get('city').errors ||
        this.legalInfoForm.get('zip').errors ||
        this.legalInfoForm.get('state').errors
      ) {
        return true;
      }
    }
    return false;
  }

  submit() {
    this.toggleLoadingSpinner.emit(true);

    //if the user has no updates to make, route directly to manual review
    if (
      this.legalInfoForm.get('hasAddress').value === 'false' &&
      this.legalInfoForm.get('hasLegal').value === 'false'
    ) {
      this.accountCenterController.checkRequirement(
        this.controller.customer.application.primaryPolicy,
        'Operations Review',
        'operations review'
      );
    } else {
      if (this.legalInfoForm.get('hasLegal').value === 'true') {
        //update the name
        this.person.firstName = this.legalInfoForm.get('legalFirstName').value;
        this.person.middleName = this.legalInfoForm.get('legalMiddleName').value;
        this.person.lastName = this.legalInfoForm.get('legalLastName').value;
      }
      if (this.legalInfoForm.get('hasAddress').value === 'true') {
        //save the address in the other field
        this.verificationAddress.street = this.legalInfoForm.get('street').value;
        this.verificationAddress.line1 = this.legalInfoForm.get('line1').value;
        this.verificationAddress.city = this.legalInfoForm.get('city').value;
        this.verificationAddress.zip = this.legalInfoForm.get('zip').value;
        this.verificationAddress.state = this.legalInfoForm.get('state').value;
      }

      if (
        this.legalInfoForm.get('hasLegal').value === 'true' ||
        this.legalInfoForm.get('hasAddress').value === 'true'
      ) {
        this.controller.publicSave();
      }

      this.submitCorrection.emit();
    }
  }

  clearPage2Data() {
    this.clearLegalName();
    this.clearPreviousAddress();
  }

  public setLegal() {
    if (this.legalInfoForm.get('hasLegal').value === 'false') {
      this.clearLegalName();
    }
  }

  clearLegalName() {
    this.legalInfoForm.get('legalFirstName').reset('');
    this.legalInfoForm.get('legalMiddleName').reset('');
    this.legalInfoForm.get('legalLastName').reset('');
  }

  public setAddress() {
    if (this.legalInfoForm.get('hasAddress').value === 'false') {
      this.clearPreviousAddress();
    }
  }

  clearPreviousAddress() {
    this.legalInfoForm.get('street').reset('');
    this.legalInfoForm.get('city').reset('');
    this.legalInfoForm.get('zip').reset('');
    this.legalInfoForm.get('state').reset('');
  }

  async setZip(): Promise<void> {
    try {
      const newZip = this.legalInfoForm.get('zip').value;

      if (newZip === '') {
        return;
      }

      const zipLookup = await this.geoChangeService.zipCodeLookup(newZip);
      const newCity = zipLookup['city'];
      const newState = zipLookup['stateCode'];
      this.legalInfoForm.get('city').patchValue(newCity);
      this.legalInfoForm.get('state').patchValue(newState);
    } catch (e) {
      this.legalInfoForm.get('zip').setErrors({ invalidZip: true });
    }
  }

  private initializeLegalInfoForm() {
    this.legalInfoForm.patchValue({
      legalFirstName: '',
      legalMiddleName: '',
      legalLastName: '',
      street: '',
      line1: '',
      city: '',
      zip: '',
      state: '',
    });
  }

  private setPersonAndAddress(): void {
    if (this.personType === 'owner') {
      this.person = this.controller.customer.application.primaryPolicy.owner;
      this.verificationAddress = this.controller.customer.application.primaryPolicy.ownerPersonVerificationAddress;
      this.nameFieldText = `Does ${this.person.firstName}, the owner, have a different legal name?`;
      this.addressText = `Has ${this.person.firstName}, the owner's, legal address changed in the past two years?`;
    } else {
      this.person = this.controller.customer.application.primaryPolicy.insured.person;
      this.verificationAddress = this.controller.customer.application.primaryPolicy.insured.verificationAddress;
      this.nameFieldText = `Does ${this.person.firstName}, the insured, have a different legal name?`;
      this.addressText = `Has ${this.person.firstName}, the insured's, legal address changed in the past two years?`;
    }
  }
}
